import {
  NBA_STAT_GROUPS,
  getNbaGridFields,
  getNbaPlayerPath,
} from "../adapters/nbaPlayerAdapter";
import { getActiveNbaFranchises } from "../utils/nbaFranchises";

const VALUE_FIELD = (f) => f.id !== "year" && f.id !== "team";

const fieldsByGroup = {
  totals: getNbaGridFields("totals")
    .filter(VALUE_FIELD)
    .map((f) => f.id),
  perGame: getNbaGridFields("perGame")
    .filter(VALUE_FIELD)
    .map((f) => f.id),
};

function buildFieldMeta(groupIds) {
  const meta = {};
  groupIds.forEach((groupId) => {
    getNbaGridFields(groupId)
      .filter(VALUE_FIELD)
      .forEach((f) => {
        if (!meta[f.id]) {
          meta[f.id] = {
            label: f.label,
            abbrev: f.label,
            rate: f.format === "pct" || f.format === "decimal1",
            format: f.format,
          };
        }
      });
  });
  return meta;
}

const fieldMeta = buildFieldMeta(["totals", "perGame"]);

const NBA_LANDING_TOTALS = {
  queryType: "career",
  startYear: 1946,
  endYear: 2026,
  selectedStats: ["pts", "trb", "ast", "g"],
  statisticalFilters: { pts: { operator: ">=", value: 10000 } },
  sortBy: "pts",
  sortDirection: -1,
  limit: 50,
  groupId: "totals",
  teams: [],
  biographicalFilters: {},
};

const NBA_LANDING_PER_GAME = {
  queryType: "career",
  startYear: 1946,
  endYear: 2026,
  selectedStats: ["pts_per_g", "trb_per_g", "ast_per_g", "g"],
  statisticalFilters: { pts_per_g: { operator: ">=", value: 20 } },
  sortBy: "pts_per_g",
  sortDirection: -1,
  limit: 50,
  groupId: "perGame",
  teams: [],
  biographicalFilters: {},
};

const recommendedByGroup = {
  totals: [
    {
      id: "pts",
      stat: "pts",
      operator: ">=",
      careerValue: 10000,
      seasonValue: 2000,
      label: "Points",
      abbrev: "PTS",
    },
    {
      id: "trb",
      stat: "trb",
      operator: ">=",
      careerValue: 8000,
      seasonValue: 1000,
      label: "Rebounds",
      abbrev: "REB",
    },
    {
      id: "ast",
      stat: "ast",
      operator: ">=",
      careerValue: 5000,
      seasonValue: 800,
      label: "Assists",
      abbrev: "AST",
    },
  ],
  perGame: [
    {
      id: "pts_per_g",
      stat: "pts_per_g",
      operator: ">=",
      careerValue: 20,
      seasonValue: 25,
      label: "Points Per Game",
      abbrev: "PPG",
    },
    {
      id: "trb_per_g",
      stat: "trb_per_g",
      operator: ">=",
      careerValue: 10,
      seasonValue: 12,
      label: "Rebounds Per Game",
      abbrev: "RPG",
    },
    {
      id: "ast_per_g",
      stat: "ast_per_g",
      operator: ">=",
      careerValue: 8,
      seasonValue: 10,
      label: "Assists Per Game",
      abbrev: "APG",
    },
  ],
};

const careerPreset = ({ id, title, subtitle, category, featured = false, groupId, stats, filters, sortBy }) => ({
  id,
  title,
  subtitle,
  category,
  featured,
  params: {
    ...NBA_LANDING_TOTALS,
    groupId,
    queryType: "career",
    selectedStats: stats,
    statisticalFilters: filters,
    sortBy,
    sortDirection: -1,
  },
});

const seasonPreset = ({ id, title, subtitle, category, featured = false, groupId, stats, filters, sortBy }) => ({
  id,
  title,
  subtitle,
  category,
  featured,
  params: {
    ...NBA_LANDING_TOTALS,
    groupId,
    queryType: "season",
    selectedStats: stats,
    statisticalFilters: filters,
    sortBy,
    sortDirection: -1,
  },
});

const perGamePreset = ({ id, title, subtitle, category, featured = false, stats, filters, sortBy }) => ({
  id,
  title,
  subtitle,
  category,
  featured,
  params: {
    ...NBA_LANDING_PER_GAME,
    groupId: "perGame",
    queryType: "career",
    selectedStats: stats,
    statisticalFilters: filters,
    sortBy,
    sortDirection: -1,
  },
});

const perGameSeasonPreset = ({ id, title, subtitle, category, featured = false, stats, filters, sortBy }) => ({
  id,
  title,
  subtitle,
  category,
  featured,
  params: {
    ...NBA_LANDING_PER_GAME,
    groupId: "perGame",
    queryType: "season",
    selectedStats: stats,
    statisticalFilters: filters,
    sortBy,
    sortDirection: -1,
  },
});

const presets = [
  careerPreset({
    id: "20k-points",
    title: "20,000+ Points",
    subtitle: "Career scoring milestone",
    category: "scoring",
    groupId: "totals",
    stats: ["pts", "g"],
    filters: { pts: { operator: ">=", value: 20000 } },
    sortBy: "pts",
  }),
  careerPreset({
    id: "30k-points",
    title: "30,000+ Points",
    subtitle: "All-time scoring elite",
    category: "scoring",
    featured: true,
    groupId: "totals",
    stats: ["pts", "g"],
    filters: { pts: { operator: ">=", value: 30000 } },
    sortBy: "pts",
  }),
  careerPreset({
    id: "15k-rebounds",
    title: "15,000+ Rebounds",
    subtitle: "All-time rebounding leaders",
    category: "glass",
    groupId: "totals",
    stats: ["trb", "g"],
    filters: { trb: { operator: ">=", value: 15000 } },
    sortBy: "trb",
  }),
  careerPreset({
    id: "10k-assists",
    title: "10,000+ Assists",
    subtitle: "Elite playmakers of all time",
    category: "playmaking",
    groupId: "totals",
    stats: ["ast", "g"],
    filters: { ast: { operator: ">=", value: 10000 } },
    sortBy: "ast",
  }),
  perGamePreset({
    id: "25-ppg",
    title: "25+ PPG Career",
    subtitle: "Sustained elite scoring average",
    category: "scoring",
    featured: true,
    stats: ["pts_per_g", "g"],
    filters: { pts_per_g: { operator: ">=", value: 25 } },
    sortBy: "pts_per_g",
  }),
  perGamePreset({
    id: "12-rpg",
    title: "12+ RPG Career",
    subtitle: "Dominant rebounders",
    category: "glass",
    stats: ["trb_per_g", "g"],
    filters: { trb_per_g: { operator: ">=", value: 12 } },
    sortBy: "trb_per_g",
  }),
  perGamePreset({
    id: "8-apg",
    title: "8+ APG Career",
    subtitle: "Elite career playmakers",
    category: "playmaking",
    stats: ["ast_per_g", "g"],
    filters: { ast_per_g: { operator: ">=", value: 8 } },
    sortBy: "ast_per_g",
  }),
  careerPreset({
    id: "50-40-90",
    title: "50/40/90 Club",
    subtitle: "FG% ≥ 50%, 3P% ≥ 40%, FT% ≥ 90% career",
    category: "shooting",
    groupId: "totals",
    stats: ["fg_pct", "fg3_pct", "ft_pct", "g"],
    filters: {
      fg_pct: { operator: ">=", value: 0.5 },
      fg3_pct: { operator: ">=", value: 0.4 },
      ft_pct: { operator: ">=", value: 0.9 },
    },
    sortBy: "fg_pct",
  }),
  seasonPreset({
    id: "2000-point-seasons",
    title: "2,000+ Point Seasons",
    subtitle: "Individual seasons with elite scoring",
    category: "seasons",
    featured: true,
    groupId: "totals",
    stats: ["pts", "g"],
    filters: { pts: { operator: ">=", value: 2000 } },
    sortBy: "pts",
  }),
  perGameSeasonPreset({
    id: "30-ppg-seasons",
    title: "30 PPG Seasons",
    subtitle: "Single seasons with 30+ PPG",
    category: "seasons",
    stats: ["pts_per_g", "g"],
    filters: { pts_per_g: { operator: ">=", value: 30 } },
    sortBy: "pts_per_g",
  }),
];

const featuredPresets = presets.filter((p) => p.featured);

export const nbaCatalog = {
  sport: "nba",
  heroSubtitle: "Browse NBA careers and seasons with visual filters and shareable URLs.",
  queryTypes: [
    { value: "career", label: "Career" },
    { value: "season", label: "Season" },
  ],
  groups: NBA_STAT_GROUPS,
  defaultGroupId: "totals",
  yearMin: 1946,
  yearMax: 2026,
  nameSortField: "bioData.displayName",
  getPlayerPath: getNbaPlayerPath,
  fieldsByGroup,
  fieldMeta,
  landingByGroup: {
    totals: NBA_LANDING_TOTALS,
    perGame: NBA_LANDING_PER_GAME,
  },
  recommendedByGroup,
  presets,
  featuredPresets,
  bioFields: [
    { value: "nameStartsWith", label: "Name starts with" },
    { value: "nameEndsWith", label: "Name ends with" },
    { value: "birthPlace", label: "Birth place" },
    { value: "position", label: "Position" },
  ],
  teams: getActiveNbaFranchises(),
};
